import {
  ApprovalMaterializedApprovalFragment,
  MaterializedApprovalFlowPolicyFragment,
  TimeoffAllowanceStatsDocument,
  TimeoffApprovalsRequestPageDocument,
  type TimeoffApprovalsRequestPageQuery,
} from 'generated/resources/graphql'
import { uniq } from 'lodash'

import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { convertDate, dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import documents from 'modules/documents/store/collections/documents'
import LeaveUtils from 'modules/timeoff/lib/LeaveUtils'
import leaves from 'modules/timeoff/store/collections/leaves'

import type { RequestComponentProps } from 'modules/notifications/pages/RequestsPage/RequestItems/RequestItem/base'
import BasePendingApproval from 'modules/notifications/pages/RequestsPage/RequestItems/RequestItem/CustomContent/PendingApprovals/BasePendingApprovals'
import { findValueInBlock } from 'modules/notifications/pages/RequestsPage/RequestItems/RequestItem/CustomContent/PendingApprovals/FindInEmailBlock'
import LeftBlock from './components/LeftBlock'
import RightBlock from './components/RightBlock'
import { CollapseContentData, EmailBlockData } from './types'

type EmployeesEmployee = NonNullable<
  TimeoffApprovalsRequestPageQuery['employees']['employees']
>[number]

const TimeoffAprovals = ({ request, index, compact, removeRequestById }: RequestComponentProps) => {
  /*
   * Get the users by the ids arg
   */
  const mapEmployeeIdsToUserNameWithMax = (
    employees: Pick<EmployeesEmployee, 'id' | 'fullName'>[],
    ids: string[],
    max = 10
  ): string[] => {
    const userNames = []
    for (const id of uniq(ids)) {
      if (userNames.length >= max) {
        break
      }

      const employee = employees.find((employee) => employee.id === id)
      if (!employee) {
        throw Error(`Error getting employees with id = ${id}`)
      }

      userNames.push(employee.fullName)
    }
    return userNames
  }

  /*
   * Object with the data of the email blocks
   */
  const getEmailBlockResources = (flow: ApprovalMaterializedApprovalFragment): EmailBlockData => {
    return {
      description: findValueInBlock(flow, 'leave-description'),
      leaveType: findValueInBlock(flow, 'leave-type'),
      from:
        findValueInBlock(flow, 'leave-start-on-datetime') ||
        findValueInBlock(flow, 'leave-start-on-date'),
      until:
        findValueInBlock(flow, 'leave-finish-on-datetime') ||
        findValueInBlock(flow, 'leave-finish-on-date'),
    }
  }

  /*
   * Information about leave overlaps
   */
  const leaveOverlaps = async (employeeId: string, data: EmailBlockData) => {
    return await leaves.rpc('overlaps', {
      employee_id: employeeId,
      from: data.from,
      to: data.until,
    })
  }

  /*
   * Documents by leave
   */
  const documentsByLeave = async (flow: ApprovalMaterializedApprovalFragment) => {
    return await documents.fetch({
      data: {
        leave_id: flow.resourceId,
      },
    })
  }

  /*
   * We clone the original request since we might need to overwrite the description
   * and we cannot overwrite the original request because of how the component is built
   */
  const clonedRequest = { ...request }

  /**
   * Fetch all the necessary resources and return them
   *
   */

  const [fetchData, {}] = useLazyQuery(TimeoffApprovalsRequestPageDocument)
  const [fetchStats, {}] = useLazyQuery(TimeoffAllowanceStatsDocument)

  const fetchResources = async (
    flow: ApprovalMaterializedApprovalFragment,
    policy?: MaterializedApprovalFlowPolicyFragment | null
  ) => {
    if (!policy?.readLeaves?.can) return { noPermission: !policy?.readLeaves?.can }

    const blockData = getEmailBlockResources(flow)
    const flowOwnerId = flow?.owner?.employee?.id ?? '0'

    const [overlaps, documents] = await Promise.all([
      leaveOverlaps(flowOwnerId, blockData),
      documentsByLeave(flow),
    ])

    const { data } = await fetchData({
      variables: {
        leaveId: flow.resourceId,
        policyId: flow?.owner?.employee?.timeoffPolicyId ?? '0',
        employeeIds: overlaps.in_team.concat(overlaps.overlaps),
      },
    })
    const leave = data?.timeoff.leavesConnection.nodes?.[0]
    const employees = data?.employees.employees || []
    const policyAllowances: { id: string; allowanceType: string }[] =
      data?.timeoff.policiesConnection.nodes?.[0]?.allowances.map((a) => {
        return { id: a.id, allowanceType: a.allowanceType }
      }) || []
    const leaveAllowanceIds: string[] = leave?.leaveType?.allowanceIds || []
    const allowance = policyAllowances.find((al) => leaveAllowanceIds.includes(al.id))

    const { data: statsData } = await fetchStats({
      variables: {
        employeeId: flowOwnerId,
        allowanceId: allowance?.id ?? '0',
        referenceDate: new Date().toISOString().split('T')[0] || '',
      },
    })

    clonedRequest.description = i18n.t('dashboard.todo.approvals.timeoff.leave_date', {
      date: convertDate(leave?.startOn || '', 'backend', 'ui_full_month_year'),
    })

    if (leave?.finishOn && leave?.finishOn !== leave.startOn) {
      clonedRequest.description = i18n.t('dashboard.todo.approvals.timeoff.leave_date_range', {
        start_date: convertDate(leave?.startOn, 'backend', 'ui_full_month_year'),
        end_date: dateToString(LeaveUtils.leaveFinishOn(leave), 'ui_full_month_year'),
      })
    }

    const availableUnits = statsData?.employees.employee?.allowanceStatsConnection?.edges?.[0]?.node
      ?.availableDays
      ? parseFloat(
          statsData?.employees.employee?.allowanceStatsConnection?.edges?.[0]?.node?.availableDays
        )
      : 0
    // If available units is a float with 0 decimals, we show it as an integer

    return {
      flowOwnerAccessId: flow.owner?.id,
      flowOwnerEmployeeId: flowOwnerId,
      documents,
      duration: LeaveUtils.leaveStringDuration(leave),
      startOn: leave?.startOn ? convertDate(leave?.startOn, 'backend', 'ui') : '',
      finishOn: dateToString(LeaveUtils.leaveFinishOn(leave), 'ui'),
      overlaps: {
        inTeam: {
          userNames: mapEmployeeIdsToUserNameWithMax(employees, overlaps.in_team),
          total: overlaps.in_team.length,
        },
        company: {
          userNames: mapEmployeeIdsToUserNameWithMax(employees, overlaps.overlaps),
          total: overlaps.overlaps.length,
        },
      },
      startTime: leave?.startTime || '',
      ...blockData,
      leaveType: leave?.leaveType?.translatedName ?? blockData?.leaveType,
      availableUnits: availableUnits,
      allowanceType: allowance?.allowanceType,
    }
  }

  return (
    <BasePendingApproval
      request={clonedRequest}
      index={index}
      compact={compact}
      getApprovalData={(flow, policy) => fetchResources(flow, policy)}
      removeRequestById={removeRequestById}
      collapseContentBlocks={(data) => ({
        leftBlock: <LeftBlock collapseContentData={data as CollapseContentData} />,
        rightBlock: <RightBlock collapseContentData={data as CollapseContentData} />,
      })}
    />
  )
}

export default TimeoffAprovals
